import React, { useMemo } from "react"
import { MdInfoOutline } from "react-icons/md"
import Button from "component/button/button"
import { ISkippedJourneyHotelApiItem } from "models/axiosModel"
import { buildSkippedHotelRows } from "component/quotation/duplicateQuotationSkippedHotelsUtil"
import styles from "component/quotation/duplicateQuotationSkippedHotelsPopup.module.scss"

type Props = {
    isOpen: boolean
    skippedHotels: ISkippedJourneyHotelApiItem[]
    onClose: () => void
    /** First sentence of the message; the create-version flow says "The new version has been created successfully." */
    createdMessage?: string
}

/**
 * "Hotel Availability Update": shown when a duplicate or a new version cleared some hotels.
 * No close icon, overlay click or Escape: only Continue dismisses it.
 */
const DuplicateQuotationSkippedHotelsPopup: React.FC<Props> = ({
    isOpen,
    skippedHotels,
    onClose,
    createdMessage = "The quotation has been created successfully.",
}) => {

    const rows = useMemo(() => buildSkippedHotelRows(skippedHotels), [skippedHotels])

    if (!isOpen || rows.length === 0) return null

    return (
        <div className={styles.overlay}>
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="duplicate-skipped-hotels-title"
                aria-describedby="duplicate-skipped-hotels-message"
            >
                <div className={styles.header}>
                    <div className={styles.iconCircle}>
                        <MdInfoOutline size={28} />
                    </div>
                    <h2 id="duplicate-skipped-hotels-title" className={styles.title}>
                        Hotel Availability Update
                    </h2>
                </div>

                <p id="duplicate-skipped-hotels-message" className={styles.message}>
                    {createdMessage} However, the following hotels are not available
                    under the applicable contracts for the updated travel dates.
                </p>

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.key}>
                                    <td>
                                        <div className={styles.hotelName}>{row.hotelName}</div>
                                        {row.roomTypeName && <div className={styles.roomTypeName}>{row.roomTypeName}</div>}
                                    </td>
                                    <td className={styles.dayCell}>{row.dayRangeLabel}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <p className={[styles.message, styles.footnote].join(" ")}>
                    The affected hotel selections have been cleared from the quotation. Please review the quotation
                    and select available hotels before proceeding.
                </p>

                <div className={styles.actions}>
                    <Button type="contained" label="Continue" onClick={onClose} />
                </div>
            </div>
        </div>
    )
}

export default DuplicateQuotationSkippedHotelsPopup
